import {computed, makeAutoObservable} from 'mobx'
import {compareChronological} from 'src/utils/month'

export class BaseOnResource {
  month: number

  monthString: string

  numericYear: number

  textYear: string

  calcPayAmount: number

  isDefault: boolean

  constructor(baseOn: any) {
    this.month = baseOn.Month
    this.monthString = baseOn.MonthString
    this.numericYear = baseOn.NumericYear ?? baseOn.Year
    this.textYear = baseOn.TextYear ?? ''
    this.calcPayAmount = baseOn.CalcPayAmount
    this.isDefault = baseOn.IsDefault

    makeAutoObservable(this)
  }
}

export class CalcPayment {
  id: number

  institutionId: number

  month: number

  monthString: string

  numericYear: number

  textYear: string

  baseOnMonths: BaseOnResource[]

  paymentAmount: number

  allPayments: CalcPayment[]

  externalPayments: ExternalPayments[]

  constructor(
    calcPayment: any,
    allPayments: CalcPayment[],
    externalPayments: ExternalPayments[] = []
  ) {
    this.id = calcPayment.Id
    this.institutionId = calcPayment.InstitutionId
    this.month = calcPayment.Month
    this.monthString = calcPayment.MonthString
    this.numericYear = calcPayment.NumericYear ?? calcPayment.Year
    this.textYear = calcPayment.TextYear ?? ''
    this.baseOnMonths = calcPayment.BaseOnMonths.map(
      (baseOn: any) => new BaseOnResource(baseOn)
    )
    this.paymentAmount = calcPayment.PaymentAmount
    this.allPayments = allPayments
    this.externalPayments = externalPayments

    makeAutoObservable(this, {
      defaultMonth: computed
    })
  }

  get defaultMonth() {
    return this.baseOnMonths.find(baseOn => baseOn.isDefault)
  }

  get calcPayAmountForDefaultMonth() {
    return this.defaultMonth?.calcPayAmount || 0
  }

  get totalCalcPayAmountPreviousMonths() {
    if (this.paymentAmount === 0) {
      return this.allPayments
        .filter(p => p.id < this.id)
        .reduce((sum, p) => sum + (p.defaultMonth?.calcPayAmount || 0), 0)
    }
    return undefined
  }

  get totalPaidPreviousMonths() {
    return this.allPayments
      .filter(p => p.id < this.id)
      .reduce((sum, p) => sum + p.paymentAmount, 0)
  }

  get totalExternalPaymentsPreviousMonths() {
    return this.externalPayments
      .filter(
        ep =>
          compareChronological(
            ep.month,
            ep.numericYear,
            this.month,
            this.numericYear
          ) <= 0
      )
      .reduce((sum, ep) => sum + ep.amount, 0)
  }

  get differences() {
    if (this.paymentAmount !== 0 || !this.totalCalcPayAmountPreviousMonths)
      return undefined
    return (
      this.totalCalcPayAmountPreviousMonths -
      this.totalPaidPreviousMonths -
      this.totalExternalPaymentsPreviousMonths
    )
  }

  get totalPaymentWithDifferences() {
    if (!this.differences) return undefined
    return this.calcPayAmountForDefaultMonth + this.differences
  }

  setAllpayments = (allPayments: CalcPayment[]) => {
    this.allPayments = allPayments
  }

  setExternalPayments = (externalPayments: ExternalPayments[]) => {
    this.externalPayments = externalPayments
  }
}

export class ExternalPayments {
  id: number

  institutionId: number

  month: number

  numericYear: number

  textYear: string

  comment?: string

  amount: number

  constructor(externalPayment: any) {
    this.id = externalPayment.Id ?? externalPayment.id
    this.institutionId =
      externalPayment.InstitutionId ?? externalPayment.institutionId
    this.month = externalPayment.Month ?? externalPayment.month

    this.numericYear =
      externalPayment.NumericYear ??
      externalPayment.Year ??
      externalPayment.numericYear ??
      externalPayment.year
    this.textYear = externalPayment.TextYear ?? externalPayment.textYear ?? ''
    this.comment = externalPayment.Comment ?? externalPayment.comment ?? ''
    this.amount = externalPayment.Amount ?? externalPayment.amount

    makeAutoObservable(this)
  }
}
